import { InputCheckbox } from '@creditornot/cb-checkbox';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import Decimal from 'decimal.js';
import React, { useState } from 'react';
import { Field, Form } from 'react-final-form';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { CampaignConfiguration } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import { ProductLine } from 'apis/restaurant-api/merchant-admin/venue/types';
import { Typography } from 'common/typography';
import { convertDecimalToMonetaryInt, convertMonetaryIntToDecimal } from 'common/utils/currencyDecimals';
import validateYupSchema from 'common/utils/validateYupSchema';
import { TERMS_AND_CONDITIONS_LINK } from 'modules/campaigns/constants';
import { useCampaignModuleContext } from 'modules/campaigns/context';
import { DiscountedItem, useDiscountedItems } from 'modules/campaigns/hooks/useDiscountedItems';
import { CampaignRulesFormValues, CampaignStatus, EndOfDayFormValues, ItemDiscountType } from 'modules/campaigns/types';
import { shakingAnimationMixin } from 'modules/campaigns/utils/shakingCssAnimation';
import { CampaignsTargetContent } from 'modules/campaigns/utils/telemetry/constants';
import { telemetryNewCampaignInteractionClickCampaignRules } from 'modules/campaigns/utils/telemetry/telemetry';
import { TestIds } from 'modules/campaigns/utils/testUtils';
import { endOfDayDiscountFormValidationSchema } from 'modules/campaigns/utils/validationSchemas/itemDiscountFormValidationSchema';

import { CampaignActionsFooter } from '../../CampaignActionsFooter/CampaignActionsFooter';
import CampaignFormLayout from '../../CampaignFormLayout';
import CopyCampaignIdButton from '../../CopyCampaignIdButton';
import { EndOfDayBudgetDetails } from '../../budget-section/EndOfDayBudgetDetails';
import { DiscountedItemFormItem } from '../../discounted-item-section/DiscountedItemFormItem/DiscountedItemFormItem';
import { DiscountedItemsFormItem } from '../../discounted-items-section/DiscountedItemsFormItem';
import { ItemDiscountTypeFormItem } from '../../item-discount-type-section/ItemDiscountTypeFormItem/ItemDiscountTypeFormItem';
import { ItemDiscountPreview } from '../../preview/ItemDiscount/ItemDiscountPreview';
import ItemDiscountVariantText from '../../preview/ItemDiscount/ItemDiscountVariantText';
import { EndOfDayCampaignBanner } from '../../preview/PromotionBanner/EndOfDayCampaignBanner';
import RulesFormItem from '../../rules-section/RulesFormItem';
import { CampaignSchedulingFormItem } from '../../scheduling-section/CampaignSchedulingFormItem/CampaignSchedulingFormItem';

export type EndOfDayDiscountFormProps = {
  venueDetails?: { name: Localized; short_description: Localized; listimage: string | null };
  campaignId?: string;
  campaignStatus?: CampaignStatus;
  defaultValues: EndOfDayFormValues;
  configuration: CampaignConfiguration;
  timezone: string;
  country: Country;
  isLoading: boolean;
  menuId: string;
  singleItemOnly: boolean;
  productLines: ProductLine[];
  onSubmit: (values: EndOfDayFormValues) => void;
  onClone?: (campaignId: string) => void;
  onDelete?: (campaignId: string) => void;
  onTerminate?: (campaignId: string) => void;
  onCancelEdit?: () => void;
};

const StyledRoot = styled.div`
  max-width: 1120px;
  width: 100%;
  margin: 0 auto;
`;

const StyledCheckboxLabel = styled.label<{ isShaking?: boolean }>`
  display: flex;
  gap: 8px;
  color: ${cbSemanticCssVars.textPrimary};
  ${({ isShaking }) => (isShaking ? shakingAnimationMixin : 'animation: none')}
`;

const StyledAgreementsWrapper = styled.div`
  display: flex;
  border-radius: 8px;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  padding: 16px 0;
`;

const calculateBudget = (
  {
    amount = 0,
    discountType,
    endOfDayItemsCount = 0,
    itemIds = [],
    percentage = 0,
  }: Pick<EndOfDayFormValues, 'amount' | 'discountType' | 'endOfDayItemsCount' | 'itemIds' | 'percentage'>,
  discountedItems: DiscountedItem[],
  currency: Currency,
  singleItemOnly: boolean,
) => {
  if (discountType === ItemDiscountType.AMOUNT_OFF) {
    const amountMonetary = convertDecimalToMonetaryInt(amount, currency);
    const budgetMonetary = Decimal.mul(amountMonetary, endOfDayItemsCount).ceil().toNumber();
    return convertMonetaryIntToDecimal(budgetMonetary, currency);
  }

  const selectedItemIds = singleItemOnly ? itemIds.slice(0, 1) : itemIds;
  const highestItemPrice = discountedItems.reduce((highestPrice, item) => {
    if (selectedItemIds.includes(item.id)) {
      const price = parseFloat(item.baseprice.amount);
      return price > highestPrice ? price : highestPrice;
    }
    return highestPrice;
  }, 0);
  const highesItemPriceMonetary = convertDecimalToMonetaryInt(highestItemPrice, currency);

  const budgetMonetary = Decimal.mul(Decimal.mul(highesItemPriceMonetary, percentage).div(100), endOfDayItemsCount)
    .ceil()
    .toNumber();

  return convertMonetaryIntToDecimal(budgetMonetary, currency);
};

/**
 * This form mostly replicated the ItemDiscountForm, but with some differences:
 *   audience is always everyone
 *   campaign end date is always set to the end of day
 *   text for the banner is different
 *   campaign budget is calculated automatically and user only sets items count and discount:
 *     item discount = fixed discount OR item price * percentage discount
 *     =>
 *     budget = item discount * items count
 */
const EndOfDayDiscountForm = ({
  venueDetails,
  campaignId,
  campaignStatus,
  defaultValues,
  isLoading,
  menuId,
  singleItemOnly,
  configuration,
  timezone,
  country,
  productLines,
  onClone,
  onDelete,
  onSubmit,
  onTerminate,
  onCancelEdit,
}: EndOfDayDiscountFormProps) => {
  const { currency } = useCampaignModuleContext();
  const [shouldShake, setShouldShake] = useState<boolean>(false);

  const isExistingRunningCampaign = campaignStatus === CampaignStatus.RUNNING && campaignId !== undefined;

  const { discountedItems, isLoadingMenuData, hasErrorMenuData } = useDiscountedItems(menuId);

  const handleSubmitCampaign = (values: EndOfDayFormValues) => {
    if (values.agreementTermsAndConditions) {
      const totalBudget = calculateBudget(values, discountedItems, currency, singleItemOnly);

      return onSubmit({
        ...values,
        campaignId,
        itemIds: singleItemOnly ? values.itemIds.slice(0, 1) : values.itemIds,
        totalBudgetSum: totalBudget,
      });
    }

    setShouldShake(false);
    // Timeout to prevent react from batching state setters
    setTimeout(() => setShouldShake(true), 0);
  };

  const minPercentage = configuration.item_discount?.percentage?.min_percentage || 0;
  const maxPercentage = configuration.item_discount?.percentage?.max_percentage || 100;

  const handleValidate = (values: EndOfDayFormValues) => {
    const itemIds = singleItemOnly ? values.itemIds.slice(0, 1) : values.itemIds;
    const lowestItemPrice = discountedItems.reduce<number | null>((lowestPrice, item) => {
      if (itemIds.includes(item.id)) {
        const price = parseFloat(item.baseprice.amount);
        return lowestPrice === null || price < lowestPrice ? price : lowestPrice;
      }
      return lowestPrice;
    }, null);
    const maxAmount =
      values.itemTriggerCount && lowestItemPrice ? values.itemTriggerCount * lowestItemPrice : undefined;
    return validateYupSchema(endOfDayDiscountFormValidationSchema, {
      minPercentage,
      maxPercentage,
      maxAmount,
    })(values);
  };

  return (
    <StyledRoot data-testid="item-discount-form">
      <Form<EndOfDayFormValues>
        onSubmit={handleSubmitCampaign}
        initialValues={defaultValues}
        validate={values => handleValidate(values)}
      >
        {({ handleSubmit, form, values, errors, submitFailed }) => {
          const campaignBanner = !!values.itemIds.length && !!values.itemTriggerCount && (
            <EndOfDayCampaignBanner>
              <ItemDiscountVariantText
                itemIds={values.itemIds}
                menuId={menuId}
                itemTriggerCount={values.itemTriggerCount}
                discountConfig={{
                  discountType: values.discountType,
                  amount: errors?.amount ? 0 : values.amount ?? 0,
                  percentage: errors?.percentage ? 0 : values.percentage ?? 0,
                  freeItemsCount: values.freeItemsCount,
                }}
                isEndOfDayCampaign
              />
            </EndOfDayCampaignBanner>
          );

          return (
            <CampaignFormLayout
              isFloatingBannerEnabled={!!values.itemIds.length}
              preview={
                <ItemDiscountPreview
                  venueDetails={venueDetails}
                  itemIds={values.itemIds}
                  menuId={menuId}
                  banner={campaignBanner}
                  startDate={values.startDate}
                  endDate={values.endDate}
                  timezone={timezone}
                  totalBudgetSum={values.totalBudgetSum}
                  firstPurchaseOnly={values.firstPurchaseOnly}
                  allowUnlimitedBudget={false}
                />
              }
              onFormSubmit={handleSubmit}
              banner={campaignBanner}
              tooltip="campaigns.sidebar-preview.product.tooltip"
            >
              {singleItemOnly ? (
                <DiscountedItemFormItem
                  menuId={menuId}
                  selectedItemId={values.itemIds[0]}
                  discountedItems={discountedItems}
                  isLoadingMenuData={isLoadingMenuData}
                  onUpdate={itemId => {
                    form.batch(() => {
                      form.change('itemIds', itemId ? [itemId] : []);
                      form.change('itemTriggerCount', 1);
                      form.change('freeItemsCount', 1);
                      form.change('endOfDayItemsCount', 1);
                    });
                  }}
                  errors={submitFailed && errors?.itemIds?.id ? { itemId: errors.itemIds } : undefined}
                  disabled={isExistingRunningCampaign}
                  isEndOfDayCampaign
                />
              ) : (
                <DiscountedItemsFormItem
                  selectedItemIds={values.itemIds}
                  onChange={itemIds => {
                    form.batch(() => {
                      form.change('itemIds', itemIds);
                      form.change('itemTriggerCount', 1);
                      form.change('freeItemsCount', 1);
                      form.change('endOfDayItemsCount', 1);
                    });
                  }}
                  discountedItems={discountedItems}
                  isLoadingMenuData={isLoadingMenuData}
                  hasErrorMenuData={!!hasErrorMenuData}
                  disabled={isExistingRunningCampaign}
                  errors={submitFailed && errors?.itemIds ? errors : undefined}
                  isEndOfDayCampaign
                />
              )}
              <ItemDiscountTypeFormItem
                country={country}
                minPercentage={minPercentage}
                maxPercentage={maxPercentage}
                productLines={productLines}
                disabled={isExistingRunningCampaign || !values.itemIds.length}
                isEndOfDayCampaign
              />
              <RulesFormItem
                isChangeButtonHidden
                data-testid={TestIds.FORM.RULES.WRAPPER}
                initialValues={{
                  firstPurchaseOnly: values.firstPurchaseOnly,
                  usageType: values.usageType,
                  perUserUsageLimit: values.perUserUsageLimit,
                }}
                onUpdate={(values: CampaignRulesFormValues) => {
                  form.batch(() => {
                    form.change('firstPurchaseOnly', values.firstPurchaseOnly);
                    form.change('usageType', values.usageType);
                    form.change('perUserUsageLimit', values.perUserUsageLimit);
                  });
                }}
                campaignType={defaultValues.campaignType}
                isExistingRunningCampaign={isExistingRunningCampaign}
                isNewCampaignRoute={!campaignId}
              />
              <EndOfDayBudgetDetails
                budget={calculateBudget(values, discountedItems, currency, singleItemOnly)}
                tooltip={{ description: <FormattedMessage id="campaigns.form.budget.tooltip" /> }}
                data-testid={TestIds.FORM.BUDGET.WRAPPER}
              />
              <CampaignSchedulingFormItem
                data-testid={TestIds.FORM.SCHEDULING.WRAPPER}
                timezone={timezone}
                country={country}
                startDate={values.startDate}
                endDate={values.endDate}
                weeklySchedule={values.weeklySchedule}
                campaignType={defaultValues.campaignType}
                isExistingRunningCampaign={isExistingRunningCampaign}
                isNewCampaignRoute={!campaignId}
                isEndOfDayCampaign
                onUpdate={formData => {
                  form.batch(() => {
                    form.change('startDate', formData.startDate);
                    form.change('endDate', formData.endDate);
                    form.change('weeklySchedule', formData.weeklySchedule);
                  });
                }}
              />
              <StyledAgreementsWrapper data-testid={TestIds.FORM.AGREEMENTS.WRAPPER}>
                <Field<EndOfDayFormValues['agreementTermsAndConditions']>
                  name="agreementTermsAndConditions"
                  type="checkbox"
                >
                  {({ input }) => (
                    <StyledCheckboxLabel isShaking={shouldShake && !input.checked}>
                      <InputCheckbox
                        name={input.name}
                        checked={input.checked}
                        onChange={input.onChange}
                        disabled={!!campaignId}
                      />
                      <Typography.Body3>
                        <FormattedMessage
                          id="campaigns.agreements.terms-and-conditions-statement"
                          values={{
                            link: (
                              <a href={TERMS_AND_CONDITIONS_LINK} target="_blank" rel="noreferrer">
                                <FormattedMessage id="campaigns.agreements.terms-and-conditions-link" />
                              </a>
                            ),
                          }}
                        />
                      </Typography.Body3>
                    </StyledCheckboxLabel>
                  )}
                </Field>
              </StyledAgreementsWrapper>

              <CampaignActionsFooter
                campaignId={campaignId}
                isLoading={isLoading}
                availableActions={undefined}
                onDelete={onDelete}
                onTerminate={onTerminate}
                onClone={onClone}
                onUpdate={() => handleSubmit()}
                onCreate={async () => {
                  if (!campaignId) {
                    telemetryNewCampaignInteractionClickCampaignRules(
                      CampaignsTargetContent.NEW.CREATE_CAMPAIGN,
                      defaultValues.campaignType,
                    );
                  }
                  await handleSubmit();
                }}
                onCancel={onCancelEdit}
                isEditMode
              />

              {campaignId && <CopyCampaignIdButton dataTestId={TestIds.FORM.ID.WRAPPER} campaignId={campaignId} />}
            </CampaignFormLayout>
          );
        }}
      </Form>
    </StyledRoot>
  );
};

export default EndOfDayDiscountForm;
